import dayjs from "dayjs";
import { isStepFailure } from "../../../../utils/dataLayerGuard";
import { withoutExcludedTags } from "../TagMonitorSection";

// Logic behind the quick actions on the homepage, one block per action

// Yesterday, the last full day, is compared with the average calls of the 7 days before
export const AVERAGE_DAYS = 7;
// A volume drop counts from 25% below the average, for tags with on average at least 100 calls per day
const MIN_DROP_PERCENTAGE = 25;
const MIN_AVERAGE_CALLS = 100;
// Maximum number of tag names or IDs mentioned in a description
const MAX_EXAMPLE_TAGS = 3;
// Error types of the dataLayer checks, the network (flow monitor) checks have their own types
const DATALAYER_ERROR_TYPES = ["incorrect_parameters", "missing_object"];

// "12, 34, 56", with "…" when there are more values than examples
const formatExamples = (values) => {
  const uniqueValues = [...new Set(values)];
  const examples = uniqueValues.slice(0, MAX_EXAMPLE_TAGS).join(", ");

  return uniqueValues.length > MAX_EXAMPLE_TAGS ? `${examples}, …` : examples;
};

// ---------------------------------------------------------------------------
// Tags of the period, used by the tag actions
// ---------------------------------------------------------------------------

const hasTagName = (tagName) => Boolean(tagName) && tagName !== "undefined";

// One entry per tag with its name and calls per day: { side, tagId, tagName, callsPerDay }
export const getTags = (context) => {
  const tags = new Map();

  ["client", "server"].forEach((side) => {
    withoutExcludedTags(context?.[`error_query_${side}`], context).forEach((row) => {
      // Tag IDs are only unique within a GTM container
      const key = `${side}-${row.container_id}-${row.tag_id}`;
      if (!tags.has(key)) {
        tags.set(key, {
          side,
          tagId: String(row.tag_id),
          containerId: row.container_id,
          tagName: null,
          callsPerDay: {},
        });
      }

      const tag = tags.get(key);
      tag.key = key;
      const day = dayjs(row.timestamp).format("YYYY-MM-DD");
      tag.callsPerDay[day] = (tag.callsPerDay[day] || 0) + (row.total_calls || 0);

      // A tag renamed during the period still has older rows without a name
      if (hasTagName(row.tag_name)) tag.tagName = row.tag_name;
    });
  });

  return [...tags.values()];
};

// ---------------------------------------------------------------------------
// Action "Configure missing tag names"
// ---------------------------------------------------------------------------

export const getUnnamedTagIds = (tags) => tags.filter((tag) => !tag.tagName).map((tag) => tag.tagId);

// "3 tags only show their ID: 12, 34, 56"
export const describeUnnamedTags = (tagIds) => {
  const subject = tagIds.length === 1 ? "tag only shows its ID" : "tags only show their ID";

  return `${tagIds.length} ${subject}: ${formatExamples(tagIds)}`;
};

// ---------------------------------------------------------------------------
// Action "<tag> dropped <x>%"
// ---------------------------------------------------------------------------

// "YYYY-MM-DD" of the day the given number of days ago
const daysAgo = (days) => dayjs().subtract(days, "day").format("YYYY-MM-DD");

// Calls of yesterday and the average calls per day of the 7 days before
const getYesterdayCalls = (tag) => {
  const previousDays = Array.from({ length: AVERAGE_DAYS }, (_, index) => daysAgo(index + 2));
  const averageCalls = previousDays.reduce((total, day) => total + (tag.callsPerDay[day] || 0), 0) / AVERAGE_DAYS;

  return { averageCalls, yesterdayCalls: tag.callsPerDay[daysAgo(1)] || 0 };
};

// The tag with the biggest drop of yesterday compared with its 7-day average: { tag, dropPercentage } or null
export const getTopVolumeDrop = (tags, skippedKeys = new Set()) => {
  const drops = tags.map((tag) => {
    const { averageCalls, yesterdayCalls } = getYesterdayCalls(tag);
    const dropPercentage = averageCalls > 0 ? Math.round((1 - yesterdayCalls / averageCalls) * 100) : 0;

    return { tag, averageCalls, yesterdayCalls, dropPercentage };
  });

  // Small tags are left out, for them a few calls less already looks like a big drop
  const [topDrop] = drops
    .filter(
      (drop) =>
        drop.averageCalls >= MIN_AVERAGE_CALLS &&
        drop.dropPercentage >= MIN_DROP_PERCENTAGE &&
        !skippedKeys.has(drop.tag.key),
    )
    .sort((a, b) => b.dropPercentage - a.dropPercentage);

  return topDrop || null;
};

// ---------------------------------------------------------------------------
// Action "<n> tags inactive"
// ---------------------------------------------------------------------------

// A tag that stopped at a publish is on the inactive tags card, but was probably removed so it raises no action
export const getActionableInactiveTags = (tags) => tags.filter((tag) => !tag.stopped_at_version);

// "Not fired for a while: GA4 - purchase, Meta pixel, 2621 (GTM-A), 2621 (GTM-B), …"
export const describeInactiveTags = (tags) => {
  const names = tags.map((tag) => tag.tag_name ?? tag.tag_id);
  // The same tag in two containers counts twice in the title, so the description tells them apart
  const labels = names.map((name, index) =>
    names.filter((other) => other === name).length > 1 && tags[index].container_id
      ? `${name} (${tags[index].container_id})`
      : name,
  );
  return `Not fired for a while: ${formatExamples(labels)}`;
};

// ---------------------------------------------------------------------------
// Action "Resolve datalayer errors from last run"
// ---------------------------------------------------------------------------

// Only dataLayer errors: not the cookie and network (flow monitor) checks and not the step diagnostics
const isDataLayerError = (error) =>
  DATALAYER_ERROR_TYPES.includes(error.error_type) &&
  error.event_name !== "cookie_check" &&
  error.page_type !== "cookie_check" &&
  !isStepFailure(error);

// Number of dataLayer errors in the last run, the newest hour of the snapshot timeline (newest first), which also has
// the runs without errors
export const getLastRunErrorCount = (context) => {
  const lastRun = context?.total_events_timeline?.[0]?.date;
  if (!lastRun) return 0;

  return (context.errors || []).filter(
    (error) => isDataLayerError(error) && dayjs(error.timestamp).isSame(lastRun, "hour"),
  ).length;
};

// "12 unique errors"
export const describeLastRunErrors = (count) => `${count} unique ${count === 1 ? "error" : "errors"}`;
